import { AuthModel } from '@/http/models/auth/Auth'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { createContext, useContext, useEffect, useState } from 'react'

import { UserModel } from '@/types'

interface AuthContextType {
  user: UserModel.User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (data: any) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextType>(null!)

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<UserModel.User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()
  const location = useLocation()

  // Paths that don't require authentication
  const publicPaths = ['/login']

  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('access_token')
      if (!token) {
        if (!publicPaths.includes(location.pathname)) {
          navigate('/login')
        }
      } else {
        const response = await AuthModel.getAuthUser()
        if (response.success && response.data?.result) {
          setUser(response.data.result)
          if (location.pathname === '/login') {
            navigate('/')
          }
        } else {
          localStorage.removeItem('access_token')
          setUser(null)
          if (!publicPaths.includes(location.pathname)) {
            navigate('/login')
          }
        }
      }
    } catch (e) {
      console.error(e)
      localStorage.removeItem('access_token')
      setUser(null)
      if (!publicPaths.includes(location.pathname)) {
        navigate('/login')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (formData: any) => {
    const response = await AuthModel.login({
      body: formData,
    })

    if (response.success && response.data?.authorization) {
      const { token } = response.data.authorization
      localStorage.setItem('access_token', token)
      await checkAuth()
      toast.success('Login successful')
      navigate('/')
    } else {
      const msg = (response.error as any)?.message || 'Login failed'
      toast.error(msg)
      throw new Error(msg)
    }
  }

  const logout = () => {
    AuthModel.logout()
    localStorage.removeItem('access_token')
    setUser(null)
    navigate('/login')
    toast.success('Logged out successfully')
  }

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
